/**
 * @rogueworlds/voxel-bridge — deterministic terrain generation.
 *
 * DET-1 (CONTRACTS.md) is a hard gate on this file. It uses only `+ - * /`, `Math.imul`, bitwise
 * operators, `Math.floor` and integer literals. No `Math.sin/cos/tan/pow/exp/log/hypot/random/round`,
 * no `Date`, no `performance`, no locale APIs, no float fallback inside a hash, and no iteration
 * over an unordered collection. Every intermediate is an exact integer while it is small enough to
 * be one: the bilinear numerator peaks at 65535 * 64 * 64 = 2.7e8 and the octave sum at 7.9e5,
 * both far below 2^53, so the only float step is the final division and it is one IEEE-754
 * operation (bit-identical in V8, JSC and SpiderMonkey).
 *
 * Terrain shape:
 * - `hash32(x, y, seed)` — a 32-bit integer avalanche mix (xor-shift + `Math.imul` chain).
 * - three octaves of value noise, wavelengths 64 / 24 / 9 voxels, weights 8 / 3 / 1, lattice
 *   points hashed (not sampled from a float PRNG) and interpolated bilinearly with the weights as
 *   exact fractions of `w * w`.
 * - the weighted sum is mapped onto an integer height in [40, 120] and clamped.
 * - trees are per-column events from a second hash of `(bx, bz, seed)`: one trunk site per 400
 *   columns, only on GRASS, so `sampleBlock` inspects the 9 neighbouring columns' trunk sites.
 */

import type { BlockId } from "@rogueworlds/shared";

import { AIR, BASALT, DIRT, GRASS, LEAVES, LOG, SAND, SNOW, STONE, WATER } from "./blocks.js";

/** Water fills every column voxel with `y > height` and `y <= SEA_LEVEL`. Integer voxel Y. */
export const SEA_LEVEL = 56;

/** Inclusive bounds of `surfaceHeightAt`. */
const HEIGHT_MIN = 40;
const HEIGHT_MAX = 120;

/** Lattice values are 16-bit, so an octave result is in `0..65535`. */
const LATTICE_MAX = 65535;
const LATTICE_MASK = 0xffff;

const WAVELENGTH_BROAD = 64;
const WAVELENGTH_MID = 24;
const WAVELENGTH_FINE = 9;

const WEIGHT_BROAD = 8;
const WEIGHT_MID = 3;
const WEIGHT_FINE = 1;
const WEIGHT_TOTAL = WEIGHT_BROAD + WEIGHT_MID + WEIGHT_FINE;

/** Highest y that is still the basalt world floor (`y <= 1`); y = 0 is the bottom. */
const WORLD_FLOOR_Y = 1;
/** Voxels of DIRT directly under the top block. */
const DIRT_DEPTH = 3;
/** Top block is SAND within this many voxels of `SEA_LEVEL`. */
const SHORE_BAND = 2;
/** Top block is SNOW above this height. */
const SNOW_ABOVE = 100;

/** LOG voxels above the surface block. */
const TRUNK_HEIGHT = 5;
/** Leaf layers on top of the trunk (two 3x3 layers, then one single leaf). */
const CANOPY_LAYERS = 2;
/** Roughly one trunk site per this many columns. */
const TREE_SPACING = 400;
/** Salt separating the tree-site hash from the terrain hash. */
const TREE_SALT = 0x5bf03635;

/**
 * 32-bit integer avalanche mix of two coordinates and a seed. Integer-only: `Math.imul` keeps the
 * 32-bit product exact, `>>>` and `^` are int32/uint32 operations, and the result is masked
 * unsigned so it is always in `0..4294967295`.
 */
export function hash32(x: number, y: number, seed: number): number {
  let h = (seed ^ 0x9e3779b9) | 0;
  h = Math.imul(h ^ (x | 0), 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h ^ (y | 0), 0xc2b2ae35);
  h ^= h >>> 16;
  h = Math.imul(h, 0x27d4eb2f);
  h ^= h >>> 15;
  return h >>> 0;
}

/** 16-bit lattice value for one noise octave at an integer lattice point. */
function latticeValue(ix: number, iz: number, wavelength: number, seed: number): number {
  // The wavelength is folded into the seed so the three octaves are independent lattices.
  return (hash32(ix, iz, seed ^ Math.imul(wavelength, 0x9e3779b1)) >>> 16) & LATTICE_MASK;
}

/**
 * Bilinear value noise for one octave, in `0..65535`. `(bx, bz)` may be negative:
 * `Math.floor(bx / wavelength)` is a correct floor division, and `fx`/`fz` stay in
 * `0..wavelength-1`, which is what makes neighbouring columns differ only by a small amount.
 */
function octaveValue(bx: number, bz: number, wavelength: number, seed: number): number {
  const ix = Math.floor(bx / wavelength);
  const iz = Math.floor(bz / wavelength);
  const fx = bx - ix * wavelength;
  const fz = bz - iz * wavelength;
  const v00 = latticeValue(ix, iz, wavelength, seed);
  const v10 = latticeValue(ix + 1, iz, wavelength, seed);
  const v01 = latticeValue(ix, iz + 1, wavelength, seed);
  const v11 = latticeValue(ix + 1, iz + 1, wavelength, seed);
  const wx = wavelength - fx;
  const wz = wavelength - fz;
  // Exact integer numerator; the division is the single float operation in the whole sampler.
  const weighted = v00 * wx * wz + v10 * fx * wz + v01 * wx * fz + v11 * fx * fz;
  return Math.floor(weighted / (wavelength * wavelength));
}

/** Integer voxel Y of the topmost solid voxel of that column, always in `[40, 120]`. */
export function surfaceHeightAt(bx: number, bz: number, seed: number): number {
  const broad = octaveValue(bx, bz, WAVELENGTH_BROAD, seed);
  const mid = octaveValue(bx, bz, WAVELENGTH_MID, seed);
  const fine = octaveValue(bx, bz, WAVELENGTH_FINE, seed);
  const weighted = broad * WEIGHT_BROAD + mid * WEIGHT_MID + fine * WEIGHT_FINE;
  const height =
    HEIGHT_MIN + Math.floor((weighted * (HEIGHT_MAX - HEIGHT_MIN)) / (LATTICE_MAX * WEIGHT_TOTAL));
  if (height < HEIGHT_MIN) {
    return HEIGHT_MIN;
  }
  if (height > HEIGHT_MAX) {
    return HEIGHT_MAX;
  }
  return height;
}

/** The single voxel capping a column: SNOW on peaks, SAND on the shore, GRASS elsewhere. */
function topBlockFor(height: number): BlockId {
  if (height > SNOW_ABOVE) {
    return SNOW;
  }
  if (height >= SEA_LEVEL - SHORE_BAND && height <= SEA_LEVEL + SHORE_BAND) {
    return SAND;
  }
  return GRASS;
}

/**
 * Column composition, top-down: air or water above `height`, the cap block at `height`, three DIRT,
 * STONE down to y = 2, and BASALT for the floor voxels (y <= 1 to y = 0 and below).
 */
function terrainBlockAt(by: number, height: number): BlockId {
  if (by > height) {
    return by <= SEA_LEVEL ? WATER : AIR;
  }
  if (by <= WORLD_FLOOR_Y) {
    return BASALT;
  }
  if (by === height) {
    return topBlockFor(height);
  }
  if (by >= height - DIRT_DEPTH) {
    return DIRT;
  }
  return STONE;
}

/** Hash that decides whether a column carries a tree. Distinct from the terrain hash. */
function isTrunkSite(bx: number, bz: number, seed: number): boolean {
  return hash32(bx, bz, (seed ^ TREE_SALT) | 0) % TREE_SPACING === 0;
}

/** A precomputed block of column heights covering a chunk footprint plus a 1-voxel border. */
export interface HeightMap {
  readonly heights: Int32Array;
  readonly span: number;
  readonly baseX: number;
  readonly baseZ: number;
}

/** @internal Builds the `(size + 2)^2` height map a chunk needs (footprint plus 1 voxel border). */
export function buildHeightMap(cx: number, cz: number, size: number, seed: number): HeightMap {
  const span = size + 2;
  const baseX = cx * size - 1;
  const baseZ = cz * size - 1;
  const heights = new Int32Array(span * span);
  for (let dz = 0; dz < span; dz++) {
    for (let dx = 0; dx < span; dx++) {
      heights[dz * span + dx] = surfaceHeightAt(baseX + dx, baseZ + dz, seed);
    }
  }
  return { heights, span, baseX, baseZ };
}

/** Column height through a prepared map, falling back to the direct sampler outside it. */
function heightAt(map: HeightMap | null, x: number, z: number, seed: number): number {
  if (map !== null) {
    const lx = x - map.baseX;
    const lz = z - map.baseZ;
    if (lx >= 0 && lx < map.span && lz >= 0 && lz < map.span) {
      return map.heights[lz * map.span + lx] ?? 0;
    }
  }
  return surfaceHeightAt(x, z, seed);
}

/**
 * Tree blocks at one voxel, or AIR. Only ever called for a voxel that is AIR in the bare terrain,
 * so a canopy can never carve into a hillside. The 9 neighbouring columns are inspected, each
 * gated by a single cheap hash, so a voxel far from any trunk costs 9 hashes and no column
 * height at all. Deterministic scan order: dz then dx.
 */
function treeBlockAt(bx: number, by: number, bz: number, seed: number, map: HeightMap | null): BlockId {
  for (let dz = -1; dz <= 1; dz++) {
    for (let dx = -1; dx <= 1; dx++) {
      const tx = bx + dx;
      const tz = bz + dz;
      if (!isTrunkSite(tx, tz, seed)) {
        continue;
      }
      const base = heightAt(map, tx, tz, seed);
      // Trees only ever stand on GRASS, never on WATER, SAND or BASALT.
      if (topBlockFor(base) !== GRASS) {
        continue;
      }
      if (by <= base || by > base + TRUNK_HEIGHT + CANOPY_LAYERS + 1) {
        continue;
      }
      if (dx === 0 && dz === 0 && by <= base + TRUNK_HEIGHT) {
        return LOG;
      }
      if (by <= base + TRUNK_HEIGHT + CANOPY_LAYERS) {
        // |dx| <= 1 and |dz| <= 1 by construction: this is the 3x3 canopy layer.
        return LEAVES;
      }
      if (dx === 0 && dz === 0) {
        // 1x1x1 cap on top of the two 3x3 layers.
        return LEAVES;
      }
    }
  }
  return AIR;
}

/** Shared sampling path: column composition first, then trees in the air above it. */
function sampleWith(bx: number, by: number, bz: number, seed: number, map: HeightMap | null): BlockId {
  const base = terrainBlockAt(by, heightAt(map, bx, bz, seed));
  if (base !== AIR) {
    return base;
  }
  return treeBlockAt(bx, by, bz, seed, map);
}

/** @internal `generateChunk` samples every voxel through this so it cannot drift from `sampleBlock`. */
export function sampleBlockWithHeightMap(
  bx: number,
  by: number,
  bz: number,
  seed: number,
  map: HeightMap,
): BlockId {
  return sampleWith(bx, by, bz, seed, map);
}

/**
 * The block at any absolute voxel coordinate, for any seed. Pure: byte-identical results in Node,
 * in a browser Worker and on the authority, and identical to the corresponding voxel of
 * `generateChunk` for the same `(seed, coordinates)`. Below the world floor (by < 0) it returns
 * BASALT, so a player who falls through a chunk hole still hits something solid.
 */
export function sampleBlock(bx: number, by: number, bz: number, seed: number): BlockId {
  return sampleWith(bx, by, bz, seed, null);
}
